import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowRight, BarChart3, Box, CheckCircle2, CircleDollarSign, CreditCard, ExternalLink,
  FileText, LayoutDashboard, Link2, PackageSearch, Plus, RefreshCw, Repeat2, Search, Send, ShieldAlert, Store, Truck, Undo2, WalletCards,
  Webhook, X,
} from 'lucide-react';
import QRCode from 'qrcode';
import { api, dateTime, money } from '../lib/api';
import { EmptyState, PageHeader, Status } from '../components/UI';

const request = (method, body) => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});
const text = value => String(value ?? '').trim();
const label = value => text(value).replaceAll('_', ' ').replace(/\b\w/g, match => match.toUpperCase());
const currencyMinor = value => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
};

const RESOURCES = [
  { key: 'overview', label: 'Overview', category: 'Overview', icon: LayoutDashboard },
  { key: 'orders', label: 'Orders', category: 'Payments', icon: PackageSearch },
  { key: 'transactions', label: 'Captures & refunds', category: 'Payments', icon: CreditCard },
  { key: 'payment-links', label: 'Payment links', category: 'Payments', icon: Link2 },
  { key: 'invoices', label: 'Invoices', category: 'Billing', icon: FileText },
  { key: 'subscriptions', label: 'Subscription lookup', category: 'Billing', icon: Repeat2 },
  { key: 'plans', label: 'Plans', category: 'Billing', icon: CircleDollarSign },
  { key: 'products', label: 'Products', category: 'Billing', icon: Box },
  { key: 'disputes', label: 'Disputes', category: 'Risk & service', icon: ShieldAlert },
  { key: 'reporting', label: 'Transaction reporting', category: 'Finance & reconciliation', icon: BarChart3 },
  { key: 'business-tools', label: 'Business Tools', category: 'Finance & reconciliation', icon: WalletCards },
  { key: 'webhook-events', label: 'Webhook events', category: 'Technical', icon: Webhook },
  { key: 'audit', label: 'Operations audit', category: 'Technical', icon: Activity },
];

function recordId(resource, row) {
  if (resource === 'orders') return row.paypal_order_id || row.id;
  if (resource === 'transactions') return row.paypal_transaction_id || row.id;
  if (resource === 'disputes') return row.dispute_id || row.id;
  if (resource === 'payment-links') return row.payment_link_id || row.id;
  if (resource === 'reporting') return row.transaction_id || row.transaction_info?.transaction_id || row.id;
  if (resource === 'webhook-events') return row.event_id || row.id;
  return row.id || row.plan_id || row.product_id || row.resource_id;
}
function recordTitle(resource, row) {
  if (resource === 'orders') return row.customer_name || row.customer_number || row.paypal_order_id;
  if (resource === 'transactions') return row.transaction_type === 'refund' ? 'PayPal refund' : 'PayPal capture';
  if (resource === 'invoices') return row.detail?.invoice_number || row.id;
  if (resource === 'disputes') return row.dispute_id || 'PayPal dispute';
  if (resource === 'payment-links') return row.description || row.line_items?.[0]?.name || row.payment_link_id || row.id;
  if (resource === 'reporting') return row.transaction_subject || row.transaction_id || row.transaction_info?.transaction_id || 'PayPal transaction';
  if (resource === 'plans') return row.name || row.id;
  if (resource === 'products') return row.name || row.id;
  if (resource === 'audit') return label(row.operation || 'PayPal operation');
  if (resource === 'webhook-events') return row.event_type || 'PayPal webhook';
  return recordId(resource, row);
}
function recordStatus(resource, row) {
  if (resource === 'orders') return row.status;
  if (resource === 'transactions') return row.status;
  if (resource === 'invoices') return row.status;
  if (resource === 'disputes') return row.status || row.dispute_state;
  if (resource === 'payment-links') return row.status || 'active';
  if (resource === 'reporting') return row.transaction_status || row.transaction_info?.transaction_status || 'record';
  if (resource === 'plans') return row.status;
  if (resource === 'products') return row.type || 'product';
  if (resource === 'webhook-events') return row.verification_status;
  if (resource === 'audit') return row.status || 'recorded';
  return 'record';
}
function recordAmount(resource, row) {
  if (resource === 'orders') return row.amount_minor == null ? null : money(row.amount_minor, row.currency || 'GBP');
  if (resource === 'transactions') return row.amount_minor == null ? null : money(row.amount_minor, row.currency || 'GBP');
  if (resource === 'invoices') {
    const due = row.amount?.value ?? row.due_amount?.value;
    const currency = row.amount?.currency_code || row.due_amount?.currency_code || 'GBP';
    const minor = currencyMinor(due);
    return minor == null ? null : money(minor, currency);
  }
  if (resource === 'payment-links') {
    return row.amount_minor == null ? null : money(row.amount_minor, row.currency || 'GBP');
  }
  if (resource === 'reporting') {
    const amount = row.transaction_amount || row.transaction_info?.transaction_amount;
    const minor = currencyMinor(amount?.value);
    return minor == null ? null : money(minor, amount?.currency_code || 'GBP');
  }
  if (resource === 'disputes') {
    const disputed = row.dispute_amount;
    const minor = currencyMinor(disputed?.value);
    return minor == null ? null : money(minor, disputed?.currency_code || 'GBP');
  }
  return null;
}
function recordTime(row) {
  return dateTime(row.updated_at || row.create_time || row.created_at || row.occurred_at || row.received_at || row.issue_date || row.transaction_initiation_date || row.transaction_updated_date);
}

export default function PayPalOperations() {
  const [params] = useSearchParams();
  const customerFilter = text(params.get('customer'));
  const requestedResource = text(params.get('resource'));
  const requestedAction = text(params.get('action'));
  const [config, setConfig] = useState(null);
  const [health, setHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [catalogue, setCatalogue] = useState(null);
  const [syncingCatalogue, setSyncingCatalogue] = useState(false);
  const [resource, setResource] = useState(RESOURCES.some(item => item.key === requestedResource) ? requestedResource : 'overview');
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedLive, setSelectedLive] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [createPaymentLinkOpen, setCreatePaymentLinkOpen] = useState(false);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [approvalUrl, setApprovalUrl] = useState('');
  const [createdLinkUrl, setCreatedLinkUrl] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    api('/api/paypal-operations/config').then(setConfig).catch(error => setError(error.message));
  }, []);

  useEffect(() => {
    if (requestedAction === 'create-order') setCreateOrderOpen(true);
    if (requestedAction === 'create-payment-link') setCreatePaymentLinkOpen(true);
    if (requestedAction === 'create-invoice') setCreateInvoiceOpen(true);
  }, [requestedAction]);

  useEffect(() => {
    if (!config || !['products','plans'].includes(resource)) return;
    api('/api/paypal-service-catalogue').then(setCatalogue).catch(() => setCatalogue(null));
  }, [config, resource]);

  const syncProductLibrary = async () => {
    setSyncingCatalogue(true);
    setNotice('');
    try {
      const result = await api('/api/paypal-service-catalogue/sync', request('POST', {}));
      setCatalogue(result);
      const warnings = result?.summary?.warnings?.length || result?.sync?.summary?.warnings?.length || 0;
      setNotice(warnings
        ? 'PayPal Product Library synchronised with ' + warnings + ' item requiring attention.'
        : 'PayPal Product Library is synchronised with the Sousa Murray service catalogue.');
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setSyncingCatalogue(false);
    }
  };

  const verifyConnection = async () => {
    setCheckingHealth(true);
    setNotice('');
    try {
      const result = await api('/api/paypal-operations/health');
      setHealth(result);
      setNotice(result.ok ? 'PayPal Business connection checks passed.' : 'PayPal is connected, but one or more Business Tools still need attention.');
    } catch (error) {
      setHealth(null);
      setNotice('Error: ' + error.message);
    } finally {
      setCheckingHealth(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice(current => current.startsWith('Error:') ? '' : current);
    setSelected(null);
    setSelectedLive(null);
    try {
      if (resource === 'overview') {
        const body = await api('/api/paypal-operations/overview');
        setOverview(body);
        setRows([]);
        return;
      }
      if (resource === 'subscriptions' || resource === 'business-tools') {
        setRows([]);
        return;
      }
      const supportsCustomerFilter = resource === 'orders' || resource === 'transactions' || resource === 'payment-links' || resource === 'invoices';
      const suffix = supportsCustomerFilter && customerFilter ? '?customer=' + encodeURIComponent(customerFilter) : '';
      const endpoint = resource === 'reporting'
        ? '/api/paypal-operations/reporting/transactions'
        : '/api/paypal-operations/' + resource + suffix;
      const body = await api(endpoint);
      const next =
        resource === 'orders' ? body.orders :
        resource === 'transactions' ? body.transactions :
        resource === 'payment-links' ? body.resources :
        resource === 'reporting' ? (body.transaction_details || []).map(item => ({ ...(item.transaction_info || {}), payer_info: item.payer_info || null, shipping_info: item.shipping_info || null, raw: item })) :
        resource === 'invoices' ? body.items :
        resource === 'disputes' ? body.items :
        resource === 'plans' ? body.plans :
        resource === 'products' ? body.products :
        resource === 'audit' ? body.audit :
        resource === 'webhook-events' ? body.events : [];
      setRows(Array.isArray(next) ? next : []);
    } catch (error) {
      setError(error.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [resource, customerFilter]);

  useEffect(() => {
    if (config) load();
  }, [config, load]);

  const grouped = useMemo(() => RESOURCES.reduce((result, item) => {
    (result[item.category] ||= []).push(item);
    return result;
  }, {}), []);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(row => JSON.stringify(row).toLowerCase().includes(term));
  }, [rows, query]);

  const openRecord = async row => {
    setSelected(row);
    setSelectedLive(null);
    setNotice('');
    const id = recordId(resource, row);
    if (!id) return;
    try {
      if (resource === 'orders') {
        const body = await api('/api/paypal-operations/orders/' + encodeURIComponent(id));
        setSelectedLive(body.order);
      } else if (resource === 'invoices') {
        const body = await api('/api/paypal-operations/invoices/' + encodeURIComponent(id));
        setSelectedLive(body.invoice);
      } else if (resource === 'disputes') {
        const body = await api('/api/paypal-operations/disputes/' + encodeURIComponent(id));
        setSelectedLive(body.dispute);
      } else if (resource === 'transactions') {
        const endpoint = row.transaction_type === 'refund' ? 'refunds' : 'captures';
        const body = await api('/api/paypal-operations/' + endpoint + '/' + encodeURIComponent(id));
        setSelectedLive(body.refund || body.capture);
      } else if (resource === 'payment-links') {
        const body = await api('/api/paypal-operations/payment-links/' + encodeURIComponent(id));
        setSelectedLive(body.resource);
      }
    } catch (error) {
      setNotice('Error: ' + error.message);
    }
  };

  const captureOrder = async orderId => {
    if (!window.confirm('Capture this approved PayPal order now? This sends the capture to PayPal immediately.')) return;
    setBusy(true);
    setNotice('');
    try {
      const body = await api('/api/paypal-operations/orders/' + encodeURIComponent(orderId) + '/capture', request('POST', {}));
      setSelectedLive(body.order);
      setNotice('PayPal payment captured successfully.');
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const refundCapture = async captureId => {
    const amount = window.prompt('Refund amount in pounds. Leave blank for a full refund.', '');
    if (amount === null) return;
    const reason = window.prompt('Reason shown to the payer (optional)', '') ?? '';
    const payload = { reason };
    if (amount.trim()) {
      const pounds = Number(amount);
      if (!Number.isFinite(pounds) || pounds <= 0) {
        setNotice('Error: Enter a valid refund amount.');
        return;
      }
      payload.amount_minor = Math.round(pounds * 100);
      payload.currency = selected?.currency || selectedLive?.amount?.currency_code || 'GBP';
    }
    if (!window.confirm(amount.trim() ? 'Issue this partial PayPal refund?' : 'Issue a full PayPal refund?')) return;
    setBusy(true);
    setNotice('');
    try {
      const body = await api('/api/paypal-operations/captures/' + encodeURIComponent(captureId) + '/refund', request('POST', payload));
      setSelectedLive(body.refund);
      setNotice('PayPal refund created successfully.');
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const createPaymentLink = async form => {
    setBusy(true);
    setNotice('');
    setCreatedLinkUrl('');
    try {
      const body = await api('/api/paypal-operations/payment-links', request('POST', {
        customer_reference: form.customer_reference || undefined,
        amount_minor: Math.round(Number(form.amount) * 100),
        currency: form.currency,
        description: form.description,
      }));
      setCreatedLinkUrl(body.payment_link || '');
      setCreatePaymentLinkOpen(false);
      setNotice('PayPal payment link created successfully.');
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const createInvoice = async form => {
    setBusy(true);
    setNotice('');
    try {
      const body = await api('/api/paypal-operations/invoices', request('POST', {
        customer_reference: form.customer_reference || undefined,
        detail: {
          currency_code: form.currency,
          note: form.note || 'Thank you for your business.',
          payment_term: { term_type: 'DUE_ON_RECEIPT' },
        },
        primary_recipients: [{ billing_info: { email_address: form.email } }],
        items: [{
          name: form.description,
          quantity: '1',
          unit_amount: { currency_code: form.currency, value: Number(form.amount).toFixed(2) },
        }],
      }));
      setCreateInvoiceOpen(false);
      setNotice('PayPal invoice created successfully' + (body.invoice?.id ? ' · ' + body.invoice.id : '') + '.');
      if (resource !== 'invoices') setResource('invoices');
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const disputeAction = async (disputeId, action) => {
    let payload = {};
    if (action === 'send-message') {
      const message = window.prompt('Message to the other party');
      if (!message?.trim()) return;
      payload = { message: message.trim() };
    } else if (action === 'accept-claim') {
      const note = window.prompt('Internal / PayPal note for accepting this claim', 'Claim accepted by Sousa Murray.');
      if (note === null) return;
      if (!window.confirm('Accept this PayPal claim? PayPal may automatically refund the buyer and close the dispute in their favour.')) return;
      payload = { note: note.trim() || 'Claim accepted by Sousa Murray.' };
    } else if (action === 'escalate') {
      const note = window.prompt('Reason for escalating this dispute to PayPal', 'Please review and resolve this dispute.');
      if (note === null) return;
      if (!window.confirm('Escalate this dispute to PayPal for a decision?')) return;
      payload = { note: note.trim() || 'Please review and resolve this dispute.' };
    } else if (action === 'provide-supporting-info') {
      const notes = window.prompt('Supporting information for PayPal');
      if (!notes?.trim()) return;
      payload = { notes: notes.trim() };
    }
    setBusy(true);
    setNotice('');
    try {
      await api('/api/paypal-operations/disputes/' + encodeURIComponent(disputeId) + '/' + action, request('POST', payload));
      const body = await api('/api/paypal-operations/disputes/' + encodeURIComponent(disputeId));
      setSelectedLive(body.dispute);
      setNotice('PayPal dispute action submitted successfully.');
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const addTracking = async orderId => {
    const trackingNumber = window.prompt('Tracking number');
    if (!trackingNumber) return;
    const carrier = (window.prompt('PayPal carrier code (for example ROYAL_MAIL, DPD, UPS, DHL). Use OTHER if needed.', 'ROYAL_MAIL') || '').trim().toUpperCase();
    if (!carrier) return;
    const payload = { tracking_number: trackingNumber.trim(), carrier, notify_payer: true };
    if (carrier === 'OTHER') {
      const carrierName = window.prompt('Carrier name');
      if (carrierName) payload.carrier_name_other = carrierName;
    }
    setBusy(true);
    setNotice('');
    try {
      await api('/api/paypal-operations/orders/' + encodeURIComponent(orderId) + '/tracking', request('POST', payload));
      setNotice('Shipment tracking sent to PayPal.');
      await openRecord(selected || { paypal_order_id: orderId });
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const updatePaymentLink = async row => {
    const id = recordId('payment-links', row || {});
    if (!id) return;
    const current = selectedLive || row || {};
    const line = current?.line_items?.[0] || {};
    const currentAmount = line?.unit_amount?.value || (row?.amount_minor != null ? (Number(row.amount_minor) / 100).toFixed(2) : '');
    const amount = window.prompt('Payment link amount', currentAmount);
    if (amount === null) return;
    const pounds = Number(amount);
    if (!Number.isFinite(pounds) || pounds <= 0) { setNotice('Error: Enter a valid payment link amount.'); return; }
    const description = window.prompt('Payment link description', line?.name || row?.description || 'Sousa Murray customer payment');
    if (description === null || !description.trim()) return;
    setBusy(true); setNotice('');
    try {
      const body = await api('/api/paypal-operations/payment-links/' + encodeURIComponent(id), request('PUT', {
        amount_minor: Math.round(pounds * 100),
        currency: line?.unit_amount?.currency_code || row?.currency || 'GBP',
        description: description.trim(),
      }));
      setSelectedLive(body.resource || null);
      setNotice('PayPal payment link updated.');
      await load();
    } catch (error) { setNotice('Error: ' + error.message); }
    finally { setBusy(false); }
  };

  const deletePaymentLink = async row => {
    const id = recordId('payment-links', row || {});
    if (!id || !window.confirm('Delete this PayPal payment link? The shared link will stop working.')) return;
    setBusy(true); setNotice('');
    try {
      await api('/api/paypal-operations/payment-links/' + encodeURIComponent(id), { method: 'DELETE' });
      setSelected(null); setSelectedLive(null);
      setNotice('PayPal payment link deleted.');
      await load();
    } catch (error) { setNotice('Error: ' + error.message); }
    finally { setBusy(false); }
  };

  const invoiceAction = async action => {
    const id = selectedLive?.id || recordId('invoices', selected || {});
    if (!id) return;
    if (!window.confirm(label(action) + ' this PayPal invoice?')) return;
    setBusy(true);
    setNotice('');
    try {
      await api('/api/paypal-operations/invoices/' + encodeURIComponent(id) + '/' + action, request('POST', {}));
      setNotice('PayPal invoice action completed.');
      const body = await api('/api/paypal-operations/invoices/' + encodeURIComponent(id));
      setSelectedLive(body.invoice);
      await load();
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const lookupSubscription = async () => {
    const id = subscriptionId.trim();
    if (!id) return;
    setBusy(true);
    setNotice('');
    try {
      const body = await api('/api/paypal-operations/subscriptions/' + encodeURIComponent(id));
      setSubscription(body.subscription);
    } catch (error) {
      setSubscription(null);
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const subscriptionAction = async action => {
    if (!subscription?.id) return;
    const reason = window.prompt('Reason', 'Customer Operations ' + action);
    if (reason === null) return;
    if (!window.confirm(label(action) + ' this PayPal subscription?')) return;
    setBusy(true);
    try {
      await api('/api/paypal-operations/subscriptions/' + encodeURIComponent(subscription.id) + '/' + action, request('POST', { reason }));
      const body = await api('/api/paypal-operations/subscriptions/' + encodeURIComponent(subscription.id));
      setSubscription(body.subscription);
      setNotice('PayPal subscription updated.');
    } catch (error) {
      setNotice('Error: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  return <div className="stripe-ops paypal-ops">
    <PageHeader
      eyebrow="Administration · Payments"
      title="PayPal Operations"
      description="Governed PayPal administration from Customer Operations. Orders, captures, refunds, invoices, disputes, subscriptions and signed webhook events stay connected to the CRM audit trail."
      actions={<>
        <span className={'stripe-mode ' + (config?.mode || 'unconfigured')}>
          {config?.connected ? 'PayPal ' + label(config.mode) : 'PayPal not configured'}
        </span>
        <button className="button secondary govuk-button govuk-button--secondary" onClick={verifyConnection} disabled={checkingHealth}><CheckCircle2 size={15}/>{checkingHealth?'Checking…':'Verify connection'}</button>
        <button className="button secondary govuk-button govuk-button--secondary" onClick={load} disabled={loading}><RefreshCw size={15}/>Refresh</button>
      </>}
    />

    {!config?.connected && config && <div className="operation-notice error">
      PayPal credentials are not configured yet. Add PAYPAL_CLIENT_ID and PAYPAL_CLIENT_SECRET, then set PAYPAL_ENVIRONMENT to sandbox or live.
    </div>}
    {config?.connected && !config?.webhookConfigured && <div className="operation-notice">
      PayPal API is connected, but signed webhooks are not active until PAYPAL_WEBHOOK_ID is configured for {config.webhookUrl}.
    </div>}
    {notice && <div className={'operation-notice ' + (notice.startsWith('Error:') ? 'error' : '')}>{notice}</div>}
    {approvalUrl && <div className="operation-notice"><CheckCircle2 size={16}/>PayPal checkout created. <a href={approvalUrl} target="_blank" rel="noreferrer">Open customer approval link <ExternalLink size={13}/></a></div>}
    {createdLinkUrl && <div className="operation-notice"><CheckCircle2 size={16}/>Payment link ready. <a href={createdLinkUrl} target="_blank" rel="noreferrer">Open payment link <ExternalLink size={13}/></a></div>}
    {health && <PayPalHealthSummary health={health}/>}

    <div className="stripe-ops-layout">
      <nav className="stripe-resource-nav paypal-resource-nav" aria-label="PayPal Operations sections">
        {Object.entries(grouped).map(([category, items]) => <div className="stripe-resource-group" key={category}>
          <span>{category}</span>
          {items.map(item => {
            const Icon = item.icon;
            return <button key={item.key} className={resource === item.key ? 'active' : ''} onClick={() => { setNotice(''); setResource(item.key); setQuery(''); setSubscription(null); }}>
              <Icon size={16}/><span>{item.label}</span>
            </button>;
          })}
        </div>)}
      </nav>

      <section className="stripe-resource-workspace">
        <div className="stripe-resource-heading">
          <div>
            <span className="record-kicker">{resource === 'overview' ? 'PayPal control centre' : 'PayPal REST API'}</span>
            <h2 className="govuk-heading-m">{RESOURCES.find(item => item.key === resource)?.label}</h2>
            <p className="govuk-body">{resource === 'overview'
              ? 'Live operational view of PayPal activity, payment volume, account health and the work that needs attention.'
              : resource === 'orders'
              ? 'CRM-linked PayPal orders are created server-side and retained for reconciliation with Customer360.'
              : resource === 'transactions'
                ? 'Captured payments, refunds and reconciled Virtual Terminal payments linked to the CRM.'
                : resource === 'payment-links'
                  ? 'Create and manage PayPal-hosted Payment Links. Customer-linked links remain visible in Customer360.'
                  : resource === 'reporting'
                    ? 'Live PayPal Transaction Search data for reconciliation. Reporting can take time to reflect newly executed payments.'
                    : resource === 'business-tools'
                      ? 'Use PayPal Business Tools in Customer Operations wherever PayPal exposes a supported API or hosted field integration; only provider-controlled settings and Virtual Terminal open in PayPal.'
                      : resource === 'subscriptions'
                        ? 'Retrieve a known PayPal subscription ID and apply supported lifecycle actions.'
                        : 'Live or governed PayPal records for Customer Operations.'}</p>
          </div>
          <div className="stripe-resource-actions">
            {resource === 'orders' && config?.permissions?.canWrite && <button className="button primary govuk-button" onClick={() => setCreateOrderOpen(true)}><Plus size={14}/>Create PayPal order</button>}
            {resource === 'payment-links' && config?.permissions?.canWrite && <button className="button primary govuk-button" onClick={() => setCreatePaymentLinkOpen(true)}><Link2 size={14}/>Create payment link</button>}
            {resource === 'invoices' && config?.permissions?.canWrite && <button className="button primary govuk-button" onClick={() => setCreateInvoiceOpen(true)}><FileText size={14}/>Create invoice</button>}
            {['products','plans'].includes(resource) && config?.permissions?.canWrite && <button className="button primary govuk-button" onClick={syncProductLibrary} disabled={syncingCatalogue}><RefreshCw size={14}/>{syncingCatalogue?'Synchronising…':'Sync Product Library'}</button>}
          </div>
        </div>

        {['products','plans'].includes(resource) && <PayPalCatalogueStatus catalogue={catalogue} syncing={syncingCatalogue}/>}
        {resource === 'overview'
          ? <PayPalOverview
              overview={overview}
              config={config}
              loading={loading}
              error={error}
              openResource={key => { setResource(key); setQuery(''); setSubscription(null); }}
              createOrder={() => setCreateOrderOpen(true)}
              createInvoice={() => setCreateInvoiceOpen(true)}
              createPaymentLink={() => setCreatePaymentLinkOpen(true)}
            />
          : resource === 'business-tools'
          ? <BusinessTools config={config}/>
          : resource === 'subscriptions'
          ? <SubscriptionLookup
              subscriptionId={subscriptionId}
              setSubscriptionId={setSubscriptionId}
              subscription={subscription}
              busy={busy}
              lookup={lookupSubscription}
              action={subscriptionAction}
              canWrite={config?.permissions?.canWrite}
            />
          : <>
              <div className="data-toolbar">
                <div className="toolbar-search"><Search size={17}/><input className="govuk-input" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search PayPal records"/></div>
                <span className="source-chip">{customerFilter && ['orders','transactions','payment-links','invoices'].includes(resource) ? 'Customer filtered · ' : ''}{filtered.length} record{filtered.length === 1 ? '' : 's'}</span>
              </div>
              {loading
                ? <EmptyState title="Loading PayPal records" body="Reading the current PayPal and CRM records through the governed server-side connection…"/>
                : error
                  ? <EmptyState title="PayPal records unavailable" body={error}/>
                  : <PayPalTable rows={filtered} resource={resource} open={openRecord}/>}
            </>}
      </section>
    </div>

    {selected && <PayPalDrawer
      resource={resource}
      row={selected}
      live={selectedLive}
      busy={busy}
      canWrite={config?.permissions?.canWrite}
      canRefund={config?.permissions?.canRefund}
      close={() => { setSelected(null); setSelectedLive(null); }}
      captureOrder={captureOrder}
      refundCapture={refundCapture}
      invoiceAction={invoiceAction}
      addTracking={addTracking}
      disputeAction={disputeAction}
      updatePaymentLink={updatePaymentLink}
      deletePaymentLink={deletePaymentLink}
    />}
    {createPaymentLinkOpen && <CreatePaymentLink
      initialCustomer={customerFilter}
      busy={busy}
      close={() => !busy && setCreatePaymentLinkOpen(false)}
      create={createPaymentLink}
    />}
    {createInvoiceOpen && <CreateInvoice
      initialCustomer={customerFilter}
      busy={busy}
      close={() => !busy && setCreateInvoiceOpen(false)}
      create={createInvoice}
    />}
    {createOrderOpen && <CreateOrder
      initialCustomer={customerFilter}
      busy={busy}
      close={() => !busy && setCreateOrderOpen(false)}
      create={async form => {
        setBusy(true);
        setNotice('');
        setApprovalUrl('');
        try {
          const body = await api('/api/paypal-operations/orders', request('POST', {
            customer_reference: form.customer_reference,
            amount_minor: Math.round(Number(form.amount) * 100),
            currency: form.currency,
            description: form.description,
            invoice_id: form.invoice_id || undefined,
            return_url: form.return_url || undefined,
            cancel_url: form.cancel_url || undefined,
          }));
          setApprovalUrl(body.approval_url || '');
          setCreateOrderOpen(false);
          setNotice('PayPal order created and linked to Customer360.');
          await load();
        } catch (error) {
          setNotice('Error: ' + error.message);
        } finally {
          setBusy(false);
        }
      }}
    />}
  </div>;
}

function PayPalOverview({ overview, config, loading, error, openResource, createOrder, createInvoice, createPaymentLink }) {
  if (loading && !overview) return <EmptyState title="Loading PayPal overview" body="Reading the current PayPal operational position from Customer Operations…"/>;
  if (error && !overview) return <EmptyState title="PayPal overview unavailable" body={error}/>;

  const summary = overview?.summary || {};
  const transactions = summary.transactions || {};
  const orders = summary.orders || {};
  const invoices = summary.invoices || {};
  const links = summary.paymentLinks || {};
  const terminal = summary.virtualTerminal || {};
  const webhooks = summary.webhooks || {};
  const disputes = summary.disputes || {};
  const activity = overview?.recentActivity || [];
  const netMinor = Number(transactions.netMinor || 0);

  const cards = [
    { label: 'Net PayPal volume', value: money(netMinor, transactions.currency || 'GBP'), detail: `${transactions.total || 0} recorded transactions · ${transactions.refunds || 0} refund${transactions.refunds === 1 ? '' : 's'}`, action: 'transactions' },
    { label: 'Orders', value: orders.total || 0, detail: `${orders.open || 0} awaiting completion · ${orders.completed || 0} completed`, action: 'orders' },
    { label: 'Invoices', value: invoices.total || 0, detail: `${invoices.open || 0} open · ${invoices.paid || 0} paid`, action: 'invoices' },
    { label: 'Payment links', value: links.active || 0, detail: `${links.total || 0} created in Customer Operations`, action: 'payment-links' },
    { label: 'Disputes', value: disputes.available ? disputes.open_count || 0 : '—', detail: disputes.available ? 'Live PayPal disputes requiring monitoring' : 'Live dispute count unavailable', action: 'disputes', attention: Number(disputes.open_count || 0) > 0 },
    { label: 'Assisted payments', value: terminal.awaiting || 0, detail: `${terminal.reconciled || 0} Virtual Terminal session${terminal.reconciled === 1 ? '' : 's'} reconciled`, action: 'business-tools', attention: Number(terminal.awaiting || 0) > 0 },
  ];

  return <div className="paypal-overview">
    {overview?.overviewWarnings?.length ? <div className="paypal-overview-degraded" role="status">
      <AlertTriangle size={16}/>
      <div><strong>Some overview metrics are temporarily unavailable.</strong><span>The rest of PayPal Operations is still available. Refresh after the database repair completes.</span></div>
    </div> : null}
    <section className="paypal-overview-hero">
      <div>
        <span className="record-kicker">Merchant connection</span>
        <h3 className="govuk-heading-s">{config?.connected ? 'PayPal Business is connected' : 'PayPal Business needs configuration'}</h3>
        <p className="govuk-body">{config?.connected
          ? `Customer Operations is using the ${label(config?.mode || overview?.mode || 'live')} PayPal environment. Payments, invoices and operational activity remain linked to the CRM audit trail.`
          : 'Connect the PayPal application credentials before staff use PayPal operational controls.'}</p>
      </div>
      <div className="paypal-overview-connection">
        <span className={config?.connected ? 'ok' : 'issue'}><b>{config?.connected ? 'Connected' : 'Not connected'}</b><small>API connection</small></span>
        <span className={config?.webhookConfigured ? 'ok' : 'issue'}><b>{config?.webhookConfigured ? 'Active' : 'Attention'}</b><small>Signed webhooks</small></span>
        <span><b>{label(config?.mode || overview?.mode || '—')}</b><small>Environment</small></span>
      </div>
    </section>

    <section className="paypal-overview-actions" aria-label="PayPal quick actions">
      <button onClick={createOrder}><WalletCards size={18}/><span><strong>New customer payment</strong><small>Create a Customer360-linked PayPal order</small></span><ArrowRight size={16}/></button>
      <button onClick={createInvoice}><FileText size={18}/><span><strong>Create invoice</strong><small>Issue a PayPal invoice from the CRM</small></span><ArrowRight size={16}/></button>
      <button onClick={createPaymentLink}><Link2 size={18}/><span><strong>Create payment link</strong><small>Make a reusable PayPal-hosted payment link</small></span><ArrowRight size={16}/></button>
      <button onClick={() => openResource('business-tools')}><WalletCards size={18}/><span><strong>Business Tools</strong><small>All connected PayPal operational controls</small></span><ArrowRight size={16}/></button>
    </section>

    <section className="paypal-overview-metrics" aria-label="PayPal operational summary">
      {cards.map(card => <button key={card.label} className={card.attention ? 'attention' : ''} onClick={() => openResource(card.action)}>
        <span>{card.label}</span>
        <strong>{card.value}</strong>
        <small>{card.detail}</small>
        <ArrowRight size={15}/>
      </button>)}
    </section>

    <div className="paypal-overview-lower">
      <section className="paypal-overview-panel">
        <header><div><span className="record-kicker">Recent operational activity</span><h3 className="govuk-heading-s">Latest staff and PayPal actions</h3></div><button className="button secondary small govuk-button govuk-button--secondary" onClick={() => openResource('audit')}>Open audit <ArrowRight size={13}/></button></header>
        <div className="paypal-activity-list">
          {activity.length ? activity.map(item => <div key={item.id}>
            <span className="paypal-activity-icon"><Activity size={15}/></span>
            <div><strong>{label(item.operation || 'PayPal operation')}</strong><small>{item.resource_type ? label(item.resource_type) : 'PayPal'}{item.resource_id ? ' · ' + item.resource_id : ''}</small></div>
            <div className="paypal-activity-meta">{item.amount_minor != null && <strong>{money(item.amount_minor,item.currency || 'GBP')}</strong>}<small>{dateTime(item.occurred_at)}</small></div>
          </div>) : <div className="paypal-overview-empty">No PayPal operational activity has been recorded yet.</div>}
        </div>
      </section>

      <section className="paypal-overview-panel">
        <header><div><span className="record-kicker">Platform health</span><h3 className="govuk-heading-s">Operational connection</h3></div><button className="button secondary small govuk-button govuk-button--secondary" onClick={() => openResource('webhook-events')}>Webhook log <ArrowRight size={13}/></button></header>
        <div className="paypal-health-facts">
          <div><span>Recorded webhooks</span><strong>{webhooks.total || 0}</strong><small>{webhooks.verified || 0} verified successfully</small></div>
          <div><span>Last webhook</span><strong>{webhooks.lastReceivedAt ? dateTime(webhooks.lastReceivedAt) : 'No events yet'}</strong><small>Signed PayPal event activity</small></div>
          <div><span>Refunded value</span><strong>{money(Number(transactions.refundedMinor || 0),transactions.currency || 'GBP')}</strong><small>Recorded completed PayPal refunds</small></div>
          <div><span>Gross paid value</span><strong>{money(Number(transactions.paidMinor || 0),transactions.currency || 'GBP')}</strong><small>Before recorded refunds</small></div>
        </div>
      </section>
    </div>
  </div>;
}

function PayPalCatalogueStatus({ catalogue, syncing }) {
  if (!catalogue) return <div className="paypal-catalogue-status loading"><RefreshCw size={16}/><span>Loading Sousa Murray Product Library mappings…</span></div>;
  const products = catalogue.products || [];
  const plans = catalogue.plans || [];
  const sync = catalogue.sync || {};
  const warnings = sync?.summary?.warnings || [];
  const productByCode = new Map(products.map(product => [product.product_code, product]));
  const expectedProducts = Number(catalogue.expectedProducts || 0);
  const expectedPlans = Number(catalogue.expectedPlans || 0);
  const complete = sync.status === 'synchronised' && products.length >= expectedProducts && plans.length >= expectedPlans && warnings.length === 0;

  const cadence = row => {
    const count = Number(row.interval_count || 1);
    const unit = text(row.interval || 'month').toLowerCase();
    if (count === 1) return unit === 'year' ? 'Annual' : unit === 'month' ? 'Monthly' : label(unit);
    return 'Every ' + count + ' ' + unit + (count === 1 ? '' : 's');
  };

  return <section className={'paypal-catalogue-status ' + (complete ? 'ok' : 'attention')}>
    <header>
      <div><span className="record-kicker">Sousa Murray catalogue authority</span><h3 className="govuk-heading-s">{complete ? 'PayPal Product Library is in sync' : syncing ? 'Synchronising PayPal Product Library' : 'PayPal Product Library needs attention'}</h3>
      <p className="govuk-body">Paid live service tiers are maintained as canonical PayPal products, with their monthly and annual billing plans underneath.</p></div>
      <Status value={complete ? 'synchronised' : sync.status || 'not synced'}/>
    </header>
    <div className="paypal-catalogue-metrics">
      <div><span>Products</span><strong>{products.length} / {expectedProducts}</strong><small>Canonical Sousa Murray paid tiers</small></div>
      <div><span>Billing plans</span><strong>{plans.length} / {expectedPlans}</strong><small>Monthly and annual plans expected</small></div>
      <div><span>Environment</span><strong>{label(catalogue.environment || '—')}</strong><small>{catalogue.configured ? 'PayPal API configured' : 'PayPal API not configured'}</small></div>
      <div><span>Last sync</span><strong>{sync.lastCompletedAt ? dateTime(sync.lastCompletedAt) : 'Not yet'}</strong><small>{warnings.length ? warnings.length + ' warning' + (warnings.length === 1 ? '' : 's') : 'No catalogue warnings'}</small></div>
    </div>
    {plans.length > 0 && <div className="paypal-catalogue-table-wrap"><table className="paypal-catalogue-table govuk-table">
      <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Product</th><th className="govuk-table__header">Plan</th><th className="govuk-table__header">Billing</th><th className="govuk-table__header">PayPal references</th></tr></thead>
      <tbody className="govuk-table__body">{plans.map(row => {
        const product = productByCode.get(row.product_code);
        return <tr className="govuk-table__row" key={row.sku}>
          <td className="govuk-table__cell"><strong>{product?.service_name || row.service || 'Sousa Murray'} — {product?.plan_name || row.plan_name || row.product_code}</strong><small>{row.product_code}</small></td>
          <td className="govuk-table__cell"><strong>{row.plan_name || row.sku}</strong><small>{row.sku}</small></td>
          <td className="govuk-table__cell"><strong>{money(Number(row.amount_minor || 0), row.currency || 'GBP')}</strong><small>{cadence(row)}</small></td>
          <td className="govuk-table__cell"><code>{row.paypal_product_id}</code><code>{row.paypal_plan_id}</code></td>
        </tr>;
      })}</tbody>
    </table></div>}
    {warnings.length > 0 && <div className="paypal-catalogue-warnings"><AlertTriangle size={15}/><span>{warnings.map(item => item.sku + ': ' + item.message).join(' · ')}</span></div>}
  </section>;
}

function PayPalTable({ rows, resource, open }) {
  if (!rows.length) return <EmptyState title="No PayPal records" body="No records are currently available for this PayPal area."/>;
  const openable = ['orders', 'transactions', 'invoices', 'disputes', 'payment-links'].includes(resource);
  return <div className="stripe-object-table-wrap"><table className="stripe-object-table govuk-table">
    <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Record</th><th className="govuk-table__header">Status</th><th className="govuk-table__header">Amount</th><th className="govuk-table__header">Context</th><th className="govuk-table__header">Updated</th><th className="govuk-table__header"></th></tr></thead>
    <tbody className="govuk-table__body">{rows.map((row, index) => {
      const id = recordId(resource, row) || String(index);
      return <tr className="govuk-table__row" key={id}>
        <td className="govuk-table__cell"><button className="stripe-record-link" disabled={!openable} onClick={() => openable && open(row)}><strong>{recordTitle(resource, row)}</strong><code>{id}</code></button></td>
        <td className="govuk-table__cell"><Status value={recordStatus(resource, row) || 'unknown'}/></td>
        <td className="govuk-table__cell">{recordAmount(resource, row) || '—'}</td>
        <td className="govuk-table__cell">{row.customer_number || row.customer_email || row.transaction_type || row.dispute_life_cycle_stage || row.event_type || row.staff_email || row.description || '—'}</td>
        <td className="govuk-table__cell">{recordTime(row)}</td>
        <td className="govuk-table__cell">{openable && <button className="button secondary small govuk-button govuk-button--secondary" onClick={() => open(row)}>Open <ArrowRight size={13}/></button>}</td>
      </tr>;
    })}</tbody>
  </table></div>;
}

function PayPalDrawer({ resource, row, live, busy, canWrite, canRefund, close, captureOrder, refundCapture, invoiceAction, addTracking, disputeAction, updatePaymentLink, deletePaymentLink }) {
  const object = live || row;
  const id = recordId(resource, row);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const paymentLinkUrl = text(object?.payment_link || row?.payment_link_url);
  const createPaymentLinkQr = async () => {
    if (!paymentLinkUrl) return;
    try {
      const dataUrl = await QRCode.toDataURL(paymentLinkUrl, { width: 520, margin: 2, errorCorrectionLevel: 'M' });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl('');
    }
  };
  const status = text(object?.status || row?.status).toUpperCase();
  const captureId = resource === 'transactions' && row.transaction_type === 'capture' ? id : object?.purchase_units?.flatMap(unit => unit?.payments?.captures || [])?.[0]?.id;
  const canCapture = resource === 'orders' && canWrite && ['APPROVED', 'PAYER_ACTION_REQUIRED'].includes(status);
  return <div className="stripe-editor-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !busy) close(); }}>
    <aside className="stripe-editor" role="dialog" aria-modal="true" aria-label="PayPal record">
      <header>
        <div><span className="record-kicker">PayPal {label(resource)}</span><h2 className="govuk-heading-m">{recordTitle(resource, row)}</h2><code>{id}</code></div>
        <button className="icon-button" onClick={close} disabled={busy}><X size={19}/></button>
      </header>
      <div className="stripe-editor-status"><Status value={recordStatus(resource, object) || 'unknown'}/>{recordAmount(resource, row) && <strong>{recordAmount(resource, row)}</strong>}</div>
      <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Provider record</h3><p className="govuk-body">This data is read from PayPal or from the CRM's signed PayPal event ledger.</p></div>
        {!live && ['orders', 'transactions', 'invoices', 'disputes', 'payment-links'].includes(resource) && <div className="stripe-lines-loading">Opening the current PayPal object…</div>}
      </section>

      {canCapture && <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Capture payment</h3><p className="govuk-body">Only capture after the payer has approved the PayPal order.</p></div>
        <button className="button primary govuk-button" disabled={busy} onClick={() => captureOrder(id)}><WalletCards size={14}/>{busy ? 'Capturing…' : 'Capture approved order'}</button>
      </section>}

      {resource === 'transactions' && row.transaction_type === 'capture' && canRefund && <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Refund</h3><p className="govuk-body">Create a full or partial PayPal refund. Refunds are written to the CRM transaction and audit ledger.</p></div>
        <button className="button danger govuk-button govuk-button--warning" disabled={busy} onClick={() => refundCapture(captureId)}><Undo2 size={14}/>{busy ? 'Refunding…' : 'Refund capture'}</button>
      </section>}

      {resource === 'orders' && canWrite && captureId && <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Shipment tracking</h3><p className="govuk-body">Add the carrier tracking number to the captured PayPal order and optionally notify the payer.</p></div>
        <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => addTracking(id)}><Truck size={14}/>Add tracking</button>
      </section>}

      {resource === 'payment-links' && canWrite && <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Payment Link controls</h3><p className="govuk-body">Update the amount/description or permanently delete this PayPal-hosted payment link.</p></div>
        <div className="stripe-action-grid">
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => updatePaymentLink(row)}><Link2 size={14}/>Edit link</button>
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy || !paymentLinkUrl} onClick={createPaymentLinkQr}><Store size={14}/>Generate QR</button>
          <button className="button danger govuk-button govuk-button--warning" disabled={busy} onClick={() => deletePaymentLink(row)}><X size={14}/>Delete link</button>
        </div>
        {qrDataUrl && <div className="paypal-link-qr"><img src={qrDataUrl} alt="QR code for PayPal payment link"/><div><strong>PayPal payment QR</strong><p className="govuk-body">Scan this code to open the PayPal-hosted payment link.</p><a className="button secondary small govuk-button govuk-button--secondary" href={qrDataUrl} download={'paypal-payment-link-'+id+'.png'}>Save QR image</a></div></div>}
      </section>}

      {resource === 'invoices' && canWrite && <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Invoice actions</h3><p className="govuk-body">Actions are sent directly to PayPal and retained in the Customer Operations audit log.</p></div>
        <div className="stripe-action-grid">
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => invoiceAction('send')}><Send size={14}/>Send</button>
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => invoiceAction('remind')}><RefreshCw size={14}/>Remind</button>
          <button className="button danger govuk-button govuk-button--warning" disabled={busy} onClick={() => invoiceAction('cancel')}><X size={14}/>Cancel</button>
        </div>
      </section>}

      {resource === 'disputes' && canWrite && <section className="stripe-editor-section">
        <div><h3 className="govuk-heading-s">Dispute response</h3><p className="govuk-body">Use the PayPal Disputes API from Customer Operations. Actions are audited and PayPal remains the authoritative dispute record.</p></div>
        <div className="stripe-action-grid">
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => disputeAction(id, 'send-message')}><Send size={14}/>Send message</button>
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => disputeAction(id, 'provide-supporting-info')}><FileText size={14}/>Supporting info</button>
          <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => disputeAction(id, 'escalate')}><AlertTriangle size={14}/>Escalate</button>
          <button className="button danger govuk-button govuk-button--warning" disabled={busy} onClick={() => disputeAction(id, 'accept-claim')}><Undo2 size={14}/>Accept claim</button>
        </div>
        <small className="stripe-rule-note">PayPal decides which actions are valid for the dispute’s current lifecycle stage. An unavailable action will be rejected by PayPal and nothing will be changed locally.</small>
      </section>}
      <details className="stripe-raw" open><summary>PayPal object</summary><pre>{JSON.stringify(object, null, 2)}</pre></details>
    </aside>
  </div>;
}

function CreateOrder({ initialCustomer = '', busy, close, create }) {
  const [form, setForm] = useState({
    customer_reference: initialCustomer,
    amount: '',
    currency: 'GBP',
    description: 'Sousa Murray customer payment',
    invoice_id: '',
    return_url: '',
    cancel_url: '',
  });
  const submit = event => {
    event.preventDefault();
    const amount = Number(form.amount);
    if (!form.customer_reference.trim() || !Number.isFinite(amount) || amount <= 0) return;
    create(form);
  };
  return <div className="stripe-editor-backdrop" role="presentation">
    <aside className="stripe-editor compact" role="dialog" aria-modal="true" aria-label="Create PayPal order">
      <header><div><span className="record-kicker">Create in PayPal</span><h2 className="govuk-heading-m">New customer payment</h2></div><button className="icon-button" onClick={close} disabled={busy}><X size={19}/></button></header>
      <form onSubmit={submit}>
        <section className="stripe-editor-section">
          <div className="stripe-editor-fields">
            <label className="stripe-field govuk-label"><span>Customer ID / number</span><input className="stripe-field-control govuk-input" required value={form.customer_reference} onChange={event => setForm(value => ({ ...value, customer_reference: event.target.value }))}/></label>
            <label className="stripe-field govuk-label"><span>Amount</span><input className="stripe-field-control govuk-input" type="number" min="0.01" step="0.01" required value={form.amount} onChange={event => setForm(value => ({ ...value, amount: event.target.value }))}/></label>
            <label className="stripe-field govuk-label"><span>Currency</span><input className="stripe-field-control govuk-input" maxLength={3} value={form.currency} onChange={event => setForm(value => ({ ...value, currency: event.target.value.toUpperCase() }))}/></label>
            <label className="stripe-field wide govuk-label"><span>Description</span><input className="stripe-field-control govuk-input" value={form.description} onChange={event => setForm(value => ({ ...value, description: event.target.value }))}/></label>
            <label className="stripe-field govuk-label"><span>Invoice/reference ID (optional)</span><input className="stripe-field-control govuk-input" value={form.invoice_id} onChange={event => setForm(value => ({ ...value, invoice_id: event.target.value }))}/></label>
            <label className="stripe-field wide govuk-label"><span>Return URL (optional, HTTPS)</span><input className="stripe-field-control govuk-input" type="url" value={form.return_url} onChange={event => setForm(value => ({ ...value, return_url: event.target.value }))}/></label>
            <label className="stripe-field wide govuk-label"><span>Cancel URL (optional, HTTPS)</span><input className="stripe-field-control govuk-input" type="url" value={form.cancel_url} onChange={event => setForm(value => ({ ...value, cancel_url: event.target.value }))}/></label>
          </div>
        </section>
        <div className="stripe-editor-save"><button className="button primary govuk-button" disabled={busy}><Plus size={15}/>{busy ? 'Creating…' : 'Create PayPal order'}</button></div>
      </form>
    </aside>
  </div>;
}

function PayPalHealthSummary({ health }) {
  const checks = health?.checks || {};
  const labels = {
    oauth: 'API authentication',
    invoicing: 'Invoicing',
    paymentLinks: 'Payment Links',
    reporting: 'Transaction reporting',
    disputes: 'Disputes',
    products: 'Products',
    plans: 'Plans',
    webhook: 'Signed webhook',
    subscriptionWebhooks: 'Subscription webhooks',
  };
  return <section className="paypal-health-summary" aria-label="PayPal connection status">
    <div><strong>{health.ok ? 'PayPal Business connection verified' : 'PayPal Business connection needs attention'}</strong><span>{health.mode ? label(health.mode) : ''}{health.checkedAt ? ' · checked ' + dateTime(health.checkedAt) : ''}</span></div>
    <div className="paypal-health-grid">
      {Object.entries(labels).map(([key,name])=><span className={checks[key]?.ok?'ok':'issue'} key={key}><b>{checks[key]?.ok?'✓':'!'}</b>{name}</span>)}
    </div>
    {!health.ok && <small>Open the relevant PayPal app/settings if a feature is not authorised. Virtual Terminal, POS and Payouts have separate account/product eligibility.</small>}
  </section>;
}

function BusinessTools({ config }) {
  const nativeTools = [
    ['Customer payments','Create and capture Customer360-linked PayPal Orders, use the embedded PayPal Card Fields terminal, and refund captured payments from the CRM.','/paypal-operations?resource=orders',WalletCards],
    ['PayPal Invoicing','Create, send, remind and cancel invoices without leaving Customer Operations.','/paypal-operations?resource=invoices',FileText],
    ['Payment Links & QR','Create, edit, delete, copy and turn PayPal Payment Links into QR codes inside the CRM.','/paypal-operations?resource=payment-links',Link2],
    ['Subscriptions & Plans','Retrieve subscriptions and manage their supported lifecycle actions inside the CRM.','/paypal-operations?resource=subscriptions',Repeat2],
    ['Disputes','Review live disputes and use supported PayPal dispute response actions from the CRM.','/paypal-operations?resource=disputes',ShieldAlert],
    ['Transaction Search & reconciliation','Search live PayPal reporting data and reconcile assisted payments.','/paypal-operations?resource=reporting',BarChart3],
    ['Shipment Tracking','Add carrier tracking to captured PayPal Orders from their CRM record.','/paypal-operations?resource=orders',Truck],
    ['Point of Sale / Tap to Pay','Prepare in-person sales, sync Shop products and reconcile PayPal POS payments.','/pos',Store],
    ['Webhooks & audit','Review signed PayPal webhook events and the staff-operation audit ledger.','/paypal-operations?resource=webhook-events',Webhook],
  ];
  const hostedTools = [
    ['Virtual Terminal','The CRM prepares the customer, amount and reconciliation record, but the official PayPal Virtual Terminal remains PayPal-hosted.',config?.businessTools?.virtualTerminal,CreditCard],
    ['Website payment settings','PayPal Account Optional and other merchant-wide website preferences are PayPal-hosted settings.',config?.businessTools?.websiteSettings,WalletCards],
    ['Users & permissions','PayPal Business account users and PayPal-side roles are managed by PayPal.',config?.businessTools?.users,ShieldAlert],
    ['Developer app settings','Credentials, product enablement, scopes and webhook registration remain in the PayPal Developer Dashboard.',config?.businessTools?.developer,Webhook],
    ['Payouts','Outbound payouts require PayPal account entitlement plus a separate Sousa Murray approval workflow before CRM initiation should be enabled.',config?.businessTools?.businessTools,Send],
  ];
  const ToolGrid=({items,external=false})=><div className="paypal-business-tools-grid">{items.map(([title,body,href,Icon])=><article className="paypal-business-tool-card" key={title}>
    <Icon size={22}/><div><strong>{title}</strong><p className="govuk-body">{body}</p><small className="paypal-tool-mode">{external?'PayPal-hosted / entitlement':'Native CRM control'}</small></div>
    {href&&<a className="button secondary small govuk-button govuk-button--secondary" href={href} target={external?'_blank':undefined} rel={external?'noreferrer':undefined}>{external?'Open PayPal':'Use in CRM'} <ExternalLink size={12}/></a>}
  </article>)}</div>;
  return <div className="stripe-object-table-wrap paypal-business-tools-workspace">
    <section><div className="stripe-resource-heading compact"><div><span className="record-kicker">Native Customer Operations controls</span><h3 className="govuk-heading-s">Use PayPal without leaving the CRM</h3><p className="govuk-body">These tools are connected through PayPal APIs, PayPal-hosted Card Fields or the governed POS integration.</p></div></div><ToolGrid items={nativeTools}/></section>
    <section><div className="stripe-resource-heading compact"><div><span className="record-kicker">PayPal-controlled surfaces</span><h3 className="govuk-heading-s">Only open PayPal where the provider requires it</h3><p className="govuk-body">These areas involve PayPal account administration, the official Virtual Terminal or account-level entitlement.</p></div></div><ToolGrid items={hostedTools} external/></section>
    <div className="stripe-rule-note"><AlertTriangle size={16}/><span>Customer Operations does not receive raw card values from PayPal Card Fields. Merchant-wide PayPal settings stay central and are not copied into individual Customer360 accounts.</span></div>
  </div>;
}
function CreatePaymentLink({ initialCustomer='', busy, close, create }) {
  const[form,setForm]=useState({customer_reference:initialCustomer,amount:'',currency:'GBP',description:'Sousa Murray customer payment'});
  const submit=e=>{e.preventDefault();if(!Number.isFinite(Number(form.amount))||Number(form.amount)<=0)return;create(form);};
  return <div className="stripe-editor-backdrop" role="presentation"><aside className="stripe-editor compact" role="dialog" aria-modal="true">
    <header><div><span className="record-kicker">PayPal Payment Links</span><h2 className="govuk-heading-m">Create payment link</h2></div><button className="icon-button" onClick={close} disabled={busy}><X size={19}/></button></header>
    <form onSubmit={submit}><section className="stripe-editor-section"><p className="govuk-body">Payment Links are reusable PayPal-hosted links. Use a PayPal order instead when you need a one-time customer-specific approval link.</p><div className="stripe-editor-fields">
      <label className="stripe-field govuk-label"><span>Customer ID / number (optional)</span><input className="stripe-field-control govuk-input" value={form.customer_reference} onChange={e=>setForm(v=>({...v,customer_reference:e.target.value}))}/></label>
      <label className="stripe-field govuk-label"><span>Amount</span><input className="stripe-field-control govuk-input" type="number" min="0.01" step="0.01" required value={form.amount} onChange={e=>setForm(v=>({...v,amount:e.target.value}))}/></label>
      <label className="stripe-field govuk-label"><span>Currency</span><input className="stripe-field-control govuk-input" maxLength={3} value={form.currency} onChange={e=>setForm(v=>({...v,currency:e.target.value.toUpperCase()}))}/></label>
      <label className="stripe-field wide govuk-label"><span>Description</span><input className="stripe-field-control govuk-input" required value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))}/></label>
    </div></section><div className="stripe-editor-save"><button className="button primary govuk-button" disabled={busy}><Link2 size={15}/>{busy?'Creating…':'Create payment link'}</button></div></form>
  </aside></div>;
}

function CreateInvoice({ initialCustomer='', busy, close, create }) {
  const[form,setForm]=useState({customer_reference:initialCustomer,email:'',amount:'',currency:'GBP',description:'Sousa Murray service',note:'Thank you for your business.'});
  useEffect(()=>{if(!initialCustomer)return;api('/api/paypal-operations/customers/'+encodeURIComponent(initialCustomer)).then(body=>{const email=body?.customer?.verified_email||body?.payerEmails?.[0]||'';if(email)setForm(v=>({...v,email}));}).catch(()=>{});},[initialCustomer]);
  const submit=e=>{e.preventDefault();if(!form.email.trim()||!Number.isFinite(Number(form.amount))||Number(form.amount)<=0)return;create(form);};
  return <div className="stripe-editor-backdrop" role="presentation"><aside className="stripe-editor compact" role="dialog" aria-modal="true">
    <header><div><span className="record-kicker">PayPal Invoicing</span><h2 className="govuk-heading-m">Create invoice</h2></div><button className="icon-button" onClick={close} disabled={busy}><X size={19}/></button></header>
    <form onSubmit={submit}><section className="stripe-editor-section"><div className="stripe-editor-fields">
      <label className="stripe-field govuk-label"><span>Customer ID / number</span><input className="stripe-field-control govuk-input" value={form.customer_reference} onChange={e=>setForm(v=>({...v,customer_reference:e.target.value}))}/></label>
      <label className="stripe-field govuk-label"><span>Recipient email</span><input className="stripe-field-control govuk-input" type="email" required value={form.email} onChange={e=>setForm(v=>({...v,email:e.target.value}))}/></label>
      <label className="stripe-field govuk-label"><span>Amount</span><input className="stripe-field-control govuk-input" type="number" min="0.01" step="0.01" required value={form.amount} onChange={e=>setForm(v=>({...v,amount:e.target.value}))}/></label>
      <label className="stripe-field govuk-label"><span>Currency</span><input className="stripe-field-control govuk-input" maxLength={3} value={form.currency} onChange={e=>setForm(v=>({...v,currency:e.target.value.toUpperCase()}))}/></label>
      <label className="stripe-field wide govuk-label"><span>Item / service</span><input className="stripe-field-control govuk-input" required value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))}/></label>
      <label className="stripe-field wide govuk-label"><span>Invoice note</span><input className="stripe-field-control govuk-input" value={form.note} onChange={e=>setForm(v=>({...v,note:e.target.value}))}/></label>
    </div></section><div className="stripe-editor-save"><button className="button primary govuk-button" disabled={busy}><FileText size={15}/>{busy?'Creating…':'Create PayPal invoice'}</button></div></form>
  </aside></div>;
}

function SubscriptionLookup({ subscriptionId, setSubscriptionId, subscription, busy, lookup, action, canWrite }) {
  return <div>
    <div className="data-toolbar">
      <div className="toolbar-search"><Search size={17}/><input className="govuk-input" value={subscriptionId} onChange={event => setSubscriptionId(event.target.value)} placeholder="PayPal subscription ID"/></div>
      <button className="button primary govuk-button" onClick={lookup} disabled={busy || !subscriptionId.trim()}>Retrieve subscription</button>
    </div>
    {!subscription
      ? <EmptyState title="Enter a PayPal subscription ID" body="The Subscriptions API does not expose the same general portfolio listing as Stripe. Retrieve the known subscription ID to manage it safely."/>
      : <div className="stripe-object-table-wrap">
          <table className="stripe-object-table govuk-table"><tbody className="govuk-table__body">
            <tr className="govuk-table__row"><th className="govuk-table__header">ID</th><td className="govuk-table__cell"><code>{subscription.id}</code></td></tr>
            <tr className="govuk-table__row"><th className="govuk-table__header">Status</th><td className="govuk-table__cell"><Status value={subscription.status}/></td></tr>
            <tr className="govuk-table__row"><th className="govuk-table__header">Plan</th><td className="govuk-table__cell">{subscription.plan_id || '—'}</td></tr>
            <tr className="govuk-table__row"><th className="govuk-table__header">Subscriber</th><td className="govuk-table__cell">{subscription.subscriber?.email_address || subscription.subscriber?.payer_id || '—'}</td></tr>
            <tr className="govuk-table__row"><th className="govuk-table__header">Start</th><td className="govuk-table__cell">{dateTime(subscription.start_time)}</td></tr>
          </tbody></table>
          {canWrite && <div className="stripe-action-grid" style={{ marginTop: 16 }}>
            <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => action('activate')}><CheckCircle2 size={14}/>Activate</button>
            <button className="button secondary govuk-button govuk-button--secondary" disabled={busy} onClick={() => action('suspend')}><RefreshCw size={14}/>Suspend</button>
            <button className="button danger govuk-button govuk-button--warning" disabled={busy} onClick={() => action('cancel')}><X size={14}/>Cancel</button>
          </div>}
          <details className="stripe-raw"><summary>Raw PayPal subscription</summary><pre>{JSON.stringify(subscription, null, 2)}</pre></details>
        </div>}
  </div>;
}
